import { aiSearchProductName } from 'utils/constants';

export const feedbackModalTitle = `Provide ${aiSearchProductName} Feedback`;
export const feedbackModalRatingSelectorTitle = `How satisfied are you with ${aiSearchProductName} (beta)?`;
export const feedbackModalFreeTextTitle = `Is there anything you expected that you didn't see from your results? (optional)`;
export const feedbackModalFreeTextPlaceholder = `e.g. add details about the expected Boolean search query`;
// Shown when the modal opens with no AI search to talk about.
export const feedbackModalGeneralFreeTextTitle = `Is there anything you'd like to tell us about ${aiSearchProductName}? (optional)`;
export const feedbackModalGeneralFreeTextPlaceholder = `e.g. what you'd like ${aiSearchProductName} to help you find`;
export const productName = 'SciLeads AI Search';

export const stageOneIntro =
  "Describe what you're looking for in plain language to get relevant results.";
export const stageOnePlaceholder = 'Search by name, topic, or keyword...';
export const orgStageOnePlaceholder = organisationName =>
  `Search within ${organisationName} by name, topic, or keyword...`;
export const disclaimer =
  'AI relies on patterns and data inputs, so errors may occur.';

export const followUpQueryRunToSee = `Run ${aiSearchProductName} to see follow-up suggestions.`;
export const followUpQueryMaxRounds =
  "You've reached the maximum number of follow-up queries. Start a new search to do further filtering.";
export const followUpQueryNotEnoughResults =
  "There weren't enough search results to provide follow-up queries.";
export const followUpQuerySavedSearch =
  'Follow up queries are unavailable for saved searches.';
export const followUpQueryNoneFound =
  "I couldn't think of any ways to refine your search further.";
export const followUpQueryError = `We were unable to generate further follow-up questions at this time. Please start a new ${aiSearchProductName}.`;
export const searchFailedError = `Something went wrong running your search. Please start a new ${aiSearchProductName}.`;
export const followUpQueryHeading =
  'Refine your search with these follow up queries';
export const followUpFetchingResults = 'Let me fetch your results.';
// Two distinct lists so the phrase shown matches what's actually happening — the search re-run
// finishes at a clearly visible moment (new results on screen), so the indicator switches lists
// right at that boundary rather than blending both into one timer-driven rotation. Ordered to
// match the real pipeline (see ai-search's followup.py / orchestrator), with the last one or two
// phrases in each list intended to hold as reassurance if that stage runs long — the indicator
// stops advancing once it reaches the end rather than looping.
export const followUpSearchingPhrases = [
  'Applying your refinements…',
  'Searching…',
  'Scanning for matches…',
  'Fetching your results…',
];
export const followUpGeneratingPhrases = [
  'Reviewing your results…',
  'Looking for ways to refine your search…',
  'Generating your follow up queries…',
  'Matching suggestions to your search…',
  'Drafting some follow-up ideas…',
];

// SCIL-17611: shown as NON-clickable guidance in the org profile modal — results
// aren't guaranteed within a single organisation, so these are inspiration for
// what to ask, not canned searches. Keyed by licence (industry = BioPharma,
// academic = Academic); dual-licence users get the industry set. Product owns
// the wording — edit the arrays freely, order is display order.
export const orgProfileExampleQueries = {
  academic: [
    'Researchers working on CRISPR gene editing',
    'Who has published on cancer immunotherapy in the last two years?',
    'Researchers with recently awarded grants in neuroscience',
  ],
  industry: [
    'Researchers working on antibody drug discovery',
    'Who has presented on oncology at recent conferences?',
    'Researchers involved in active clinical trials',
  ],
};
export const orgProfileAdvancedQueries = {
  academic: [
    'Early career researchers publishing in high-impact journals',
    'Researchers whose grants expire in the next 12 months',
    'Collaborators on international vaccine research projects',
  ],
  industry: [
    'Key opinion leaders in rare disease research',
    'Researchers with recent industry collaborations in drug development',
    'Scientists who recently published on biomarker discovery',
  ],
};

// Shown on the org profile when every filter the AI generated is one the profile
// cannot apply. The query itself is fine, so this avoids the irrelevance wording
// used when the organisation genuinely has nothing to do with the question.
// Split so the banner can break between what happened and what to do about it.
export const orgUnsupportedFiltersRedirect = organisationName => ({
  lead:
    'This search needs filters that are only available on the main search page.',
  action: `Run it there, or edit your query to search within ${organisationName}.`,
});

// Shown when the org endpoint judged the query to be about something other than
// this organisation, so editing it to be about them is the other way out.
export const orgIrrelevantRedirect = organisationName => ({
  lead: `This query is not relevant to ${organisationName}.`,
  action:
    'Edit it above to search within this organisation, or run it on the main search page instead.',
});

// On the org profile, against each filter the profile cannot apply. Shown before
// the user confirms rather than in a toast afterwards, so they can still go back.
export const orgIgnoredFilterTooltip =
  'This filter is not supported on the organisation profile page, so it will not be applied.';
